/**
 * Lightweight CLASSIC board model for AI evaluation (browser-compatible,
 * pure JS, no DOM / Node APIs).
 *
 * Scoring a candidate through the core would require applyMove's state
 * clone; this model keeps a wall set + plain pawn positions and evaluates
 * with a fast BFS instead. The wall-blocking geometry follows
 * docs/CORE_INTERFACE.md exactly; the finally chosen move is always
 * validated by the core (isLegal / applyMove), so any divergence here
 * would fail fast instead of corrupting a game.
 *
 * LIMITS (by design): this model knows nothing about character abilities
 * or tile gimmicks. It is only used when the position is "classic for
 * evaluation purposes" (no gimmicks, no unspent active abilities) — see
 * isClassicEval() in index.js. Everything else goes through the core API
 * directly (src/ai/generic.js).
 */

export const DIRS = [
  { dc: 0, dr: 1 },
  { dc: 0, dr: -1 },
  { dc: 1, dr: 0 },
  { dc: -1, dr: 0 },
];

export function wallKey(col, row, orientation) {
  return orientation + ':' + col + ',' + row;
}

/** Is the step between two adjacent cells blocked (contract §wall coords)? */
export function stepBlocked(wallSet, fromCol, fromRow, toCol, toRow) {
  if (fromCol === toCol) {
    const r = Math.min(fromRow, toRow);
    return (
      wallSet.has(wallKey(fromCol - 1, r, 'h')) ||
      wallSet.has(wallKey(fromCol, r, 'h'))
    );
  }
  const c = Math.min(fromCol, toCol);
  return (
    wallSet.has(wallKey(c, fromRow - 1, 'v')) ||
    wallSet.has(wallKey(c, fromRow, 'v'))
  );
}

/** Overlap/cross check per contract (same rules as core walls.js). */
export function wallConflicts(wallSet, col, row, orientation) {
  if (orientation === 'h') {
    return (
      wallSet.has(wallKey(col - 1, row, 'h')) ||
      wallSet.has(wallKey(col, row, 'h')) ||
      wallSet.has(wallKey(col + 1, row, 'h')) ||
      wallSet.has(wallKey(col, row, 'v'))
    );
  }
  return (
    wallSet.has(wallKey(col, row - 1, 'v')) ||
    wallSet.has(wallKey(col, row, 'v')) ||
    wallSet.has(wallKey(col, row + 1, 'v')) ||
    wallSet.has(wallKey(col, row, 'h'))
  );
}

// Reusable BFS scratch buffers (grown on demand, keyed by a generation
// stamp so no per-call allocation or clearing is needed). Safe because all
// BFS calls in this module are synchronous and never nested. Semantics are
// identical to a fresh-array BFS, so determinism is preserved.
let _stamp = new Int32Array(0); // visited marker (== _gen means visited)
let _dist = new Int32Array(0); // BFS distance per cell
let _queue = new Int32Array(0); // FIFO queue of cell indices
let _parent = new Int32Array(0); // parent index per cell (for bfsPath)
let _gen = 0;

function ensureBuffers(n) {
  if (_stamp.length < n) {
    _stamp = new Int32Array(n);
    _dist = new Int32Array(n);
    _queue = new Int32Array(n);
    _parent = new Int32Array(n);
  }
}

/**
 * BFS distance from start to any cell with row === goalRow (walls only,
 * pawns never block — same convention as core shortestPathLength).
 * Returns -1 if unreachable.
 */
export function bfsDistance(size, wallSet, start, goalRow) {
  const sr = start.row;
  const sc = start.col;
  if (sr === goalRow) return 0;
  ensureBuffers(size * size);
  const gen = ++_gen;
  const stamp = _stamp;
  const dist = _dist;
  const queue = _queue;
  const startIdx = sc * size + sr;
  stamp[startIdx] = gen;
  dist[startIdx] = 0;
  let head = 0;
  let tail = 0;
  queue[tail++] = startIdx;
  while (head < tail) {
    const idx = queue[head++];
    const col = (idx / size) | 0;
    const row = idx - col * size;
    const nd = dist[idx] + 1;
    // Inlined 4-neighbour expansion (hot path).
    if (row + 1 < size && !stepBlocked(wallSet, col, row, col, row + 1)) {
      if (row + 1 === goalRow) return nd;
      const nIdx = idx + 1;
      if (stamp[nIdx] !== gen) {
        stamp[nIdx] = gen;
        dist[nIdx] = nd;
        queue[tail++] = nIdx;
      }
    }
    if (row - 1 >= 0 && !stepBlocked(wallSet, col, row, col, row - 1)) {
      if (row - 1 === goalRow) return nd;
      const nIdx = idx - 1;
      if (stamp[nIdx] !== gen) {
        stamp[nIdx] = gen;
        dist[nIdx] = nd;
        queue[tail++] = nIdx;
      }
    }
    if (col + 1 < size && !stepBlocked(wallSet, col, row, col + 1, row)) {
      const nIdx = idx + size;
      if (stamp[nIdx] !== gen) {
        stamp[nIdx] = gen;
        dist[nIdx] = nd;
        queue[tail++] = nIdx;
      }
    }
    if (col - 1 >= 0 && !stepBlocked(wallSet, col, row, col - 1, row)) {
      const nIdx = idx - size;
      if (stamp[nIdx] !== gen) {
        stamp[nIdx] = gen;
        dist[nIdx] = nd;
        queue[tail++] = nIdx;
      }
    }
  }
  return -1;
}

/**
 * BFS with parent tracking: returns one shortest path as an array of
 * {col,row} cells from start (inclusive) to the goal row, or null.
 * Used only for wall-candidate pruning, so any one shortest path is fine.
 */
export function bfsPath(size, wallSet, start, goalRow) {
  if (start.row === goalRow) return [start];
  ensureBuffers(size * size);
  const gen = ++_gen;
  const stamp = _stamp;
  const parent = _parent;
  const queue = _queue;
  const startIdx = start.col * size + start.row;
  stamp[startIdx] = gen;
  parent[startIdx] = startIdx;
  let head = 0;
  let tail = 0;
  queue[tail++] = startIdx;
  while (head < tail) {
    const idx = queue[head++];
    const col = (idx / size) | 0;
    const row = idx - col * size;
    for (let d = 0; d < 4; d++) {
      const dc = DIRS[d].dc;
      const dr = DIRS[d].dr;
      const c = col + dc;
      const r = row + dr;
      if (c < 0 || r < 0 || c >= size || r >= size) continue;
      const nIdx = c * size + r;
      if (stamp[nIdx] === gen) continue;
      if (stepBlocked(wallSet, col, row, c, r)) continue;
      stamp[nIdx] = gen;
      parent[nIdx] = idx;
      if (r === goalRow) {
        const path = [];
        let cur = nIdx;
        while (cur !== startIdx) {
          path.push({ col: (cur / size) | 0, row: cur % size });
          cur = parent[cur];
        }
        path.push({ col: start.col, row: start.row });
        path.reverse();
        return path;
      }
      queue[tail++] = nIdx;
    }
  }
  return null;
}

/** Evaluation-only model extracted from a core state. */
export function buildModel(state) {
  const wallSet = new Set();
  for (const w of state.walls) wallSet.add(wallKey(w.col, w.row, w.orientation));
  return {
    size: state.config.boardSize,
    wallSet,
    pawns: state.players.map((p) => ({ col: p.pawn.col, row: p.pawn.row })),
    goalRows: state.players.map((p) => p.goalRow),
    wallsLeft: state.players.map((p) => p.wallsLeft),
  };
}

/** Apply a move virtually on the model; returns undo info. */
export function applyVirtual(m, playerIndex, move) {
  if (move.type === 'move') {
    const prev = m.pawns[playerIndex];
    m.pawns[playerIndex] = { col: move.to.col, row: move.to.row };
    return { type: 'move', playerIndex, prev };
  }
  const key = wallKey(move.at.col, move.at.row, move.orientation);
  m.wallSet.add(key);
  m.wallsLeft[playerIndex] -= 1;
  return { type: 'wall', playerIndex, key };
}

export function undoVirtual(m, undo) {
  if (undo.type === 'move') {
    m.pawns[undo.playerIndex] = undo.prev;
  } else {
    m.wallSet.delete(undo.key);
    m.wallsLeft[undo.playerIndex] += 1;
  }
}

/**
 * Static evaluation from `me`'s perspective:
 *   (opponent shortest path) - (my shortest path)
 *   + wallWeight * (my walls left - opponent walls left)
 * Returns null if either player has no path (i.e. the virtual wall that
 * produced this position is illegal and must be discarded).
 */
export function evalModel(m, me, wallWeight) {
  const opp = 1 - me;
  const dMe = bfsDistance(m.size, m.wallSet, m.pawns[me], m.goalRows[me]);
  if (dMe < 0) return null;
  const dOpp = bfsDistance(m.size, m.wallSet, m.pawns[opp], m.goalRows[opp]);
  if (dOpp < 0) return null;
  return dOpp - dMe + wallWeight * (m.wallsLeft[me] - m.wallsLeft[opp]);
}

/**
 * Classic pawn destinations on the model, INCLUDING the face-to-face jump
 * and diagonal-dodge rules (contract §movement). Used by the hard search
 * inside the tree, where jump tempo matters for race positions.
 */
export function modelPawnMoves(m, player) {
  const me = m.pawns[player];
  const opp = m.pawns[1 - player];
  const size = m.size;
  const out = [];
  const on = (c, r) => c >= 0 && r >= 0 && c < size && r < size;
  for (const { dc, dr } of DIRS) {
    const c = me.col + dc;
    const r = me.row + dr;
    if (!on(c, r)) continue;
    if (stepBlocked(m.wallSet, me.col, me.row, c, r)) continue;
    if (c !== opp.col || r !== opp.row) {
      out.push({ col: c, row: r });
      continue;
    }
    // Opponent adjacent: straight jump if the far cell is open ...
    const jc = c + dc;
    const jr = r + dr;
    if (on(jc, jr) && !stepBlocked(m.wallSet, c, r, jc, jr)) {
      out.push({ col: jc, row: jr });
      continue;
    }
    // ... otherwise diagonal dodges around the opponent.
    const sides =
      dc === 0
        ? [
            { dc: 1, dr: 0 },
            { dc: -1, dr: 0 },
          ]
        : [
            { dc: 0, dr: 1 },
            { dc: 0, dr: -1 },
          ];
    for (const s of sides) {
      const sc = c + s.dc;
      const sr = r + s.dr;
      if (on(sc, sr) && !stepBlocked(m.wallSet, c, r, sc, sr)) {
        out.push({ col: sc, row: sr });
      }
    }
  }
  return out;
}

/**
 * Wall candidates near both players' current shortest paths.
 *
 * For every step along either path, take the (at most two) anchors whose
 * wall would block that step, plus the cross-orientation wall at the same
 * anchors (it blocks likely detours / builds corners). Deduplicated.
 * This keeps the branching to a few dozen instead of 128 anchors.
 */
export function candidateWalls(m) {
  const seen = new Set();
  const out = [];
  const add = (col, row, orientation) => {
    if (col < 0 || row < 0 || col > m.size - 2 || row > m.size - 2) return;
    const key = wallKey(col, row, orientation);
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ col, row, orientation });
  };

  for (let p = 0; p < 2; p++) {
    const path = bfsPath(m.size, m.wallSet, m.pawns[p], m.goalRows[p]);
    if (!path) continue;
    for (let i = 0; i + 1 < path.length; i++) {
      const a = path[i];
      const b = path[i + 1];
      if (a.col === b.col) {
        // vertical step -> blocked by h walls at (col-1, minRow), (col, minRow)
        const r = Math.min(a.row, b.row);
        add(a.col - 1, r, 'h');
        add(a.col, r, 'h');
        add(a.col - 1, r, 'v');
        add(a.col, r, 'v');
      } else {
        // horizontal step -> blocked by v walls at (minCol, row-1), (minCol, row)
        const c = Math.min(a.col, b.col);
        add(c, a.row - 1, 'v');
        add(c, a.row, 'v');
        add(c, a.row - 1, 'h');
        add(c, a.row, 'h');
      }
    }
  }
  return out;
}
